// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Send, Download, Trash2, Edit3, Globe, Settings as SettingsIcon, ExternalLink } from 'lucide-react';
import { apiJSON, apiFetch } from '../config/api';
import notificationService, { handleApiError } from '../utils/notificationService';
import AISelector from './AISelector';
import api from '../services/api';
import { useAppContext } from '../context/AppContext';
import ReasoningTrace from './ReasoningTrace';
import GenUIMessagePart from '../genui/GenUIMessagePart';
import { tryExtractGenUIFromText, hideIncompleteGenui } from '../genui/tryExtractGenUIFromText';
import './IA.css';

const IA: React.FC = () => {
  const { setStreamHighlight, chatDraftQuery, setChatDraftQuery } = useAppContext();
  // État conversation
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // État `error` retiré : jamais renseigné ni rendu. Les erreurs passent par
  // `handleApiError`, qui affiche une notification.

  // Configuration RAG
  const [searchMethod, setSearchMethod] = useState('hybrid');
  const [resultsCount, setResultsCount] = useState(5);
  const [bookFilters, setBookFilters] = useState('');
  
  // Configuration IA
  const [aiProviders, setAiProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [showAISettings, setShowAISettings] = useState(false);
  const [showAISelector, setShowAISelector] = useState(false);
  
  // UI État
  const [showSidebar, setShowSidebar] = useState(true);
  const [editingTitle, setEditingTitle] = useState(null);
  const messagesEndRef = useRef(null);
  const streamCancelRef = useRef(null);
  const streamTextRef = useRef('');
  const streamRafRef = useRef(null);
  const firstStreamTokenRef = useRef(false);

  const [streamDisplay, setStreamDisplay] = useState('');
  const [reasoningMeta, setReasoningMeta] = useState(null);
  const [reasoningCollapsed, setReasoningCollapsed] = useState(false);
  const [phaseStep, setPhaseStep] = useState(0);
  const [streamingStarted, setStreamingStarted] = useState(false);
  const [streamGenuiBlocks, setStreamGenuiBlocks] = useState([]);
  const streamGenuiBlocksRef = useRef([]);
  const genuiSigRef = useRef(new Set());

  const addGenUIBlock = useCallback((payload) => {
    if (!payload || !payload.type) return;
    const sig = JSON.stringify({ type: payload.type, data: payload.data });
    if (genuiSigRef.current.has(sig)) return;
    genuiSigRef.current.add(sig);
    const block = {
      id: `genui-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      type: payload.type,
      data: payload.data,
    };
    streamGenuiBlocksRef.current = [...streamGenuiBlocksRef.current, block];
    setStreamGenuiBlocks([...streamGenuiBlocksRef.current]);
  }, []);

  // Utilisation du service de notifications centralisé
  const [toast] = useState(null);

  useEffect(() => {
    if (chatDraftQuery) {
      setNewMessage(chatDraftQuery);
      setChatDraftQuery('');
    }
  }, [chatDraftQuery, setChatDraftQuery]);

  useEffect(() => {
    return () => {
      if (streamCancelRef.current) {
        streamCancelRef.current();
        streamCancelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    loadConversations();
    loadAIProviders();
    
    // Vérifier les paramètres d'URL pour le texte sélectionné
    const urlParams = new URLSearchParams(window.location.search);
    const textFromUrl = urlParams.get('text');
    const autoSend = urlParams.get('auto') === 'true';

    // La Bibliothèque et le Lecteur ouvrent /ia?book=<id> pour « interroger ce
    // livre ». Ce paramètre n'était pas lu : la question partait sur tout le
    // corpus, et `bookFilters` — pourtant transmis à l'API — restait vide faute
    // du moindre contrôle pour le renseigner.
    const bookFromUrl = urlParams.get('book') || urlParams.get('book_id');
    if (bookFromUrl) {
      setBookFilters(bookFromUrl);
    }

    if (textFromUrl) {
      // Pré-remplir le champ avec le texte du lecteur
      setNewMessage(textFromUrl);
      
      // Nettoyer l'URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      notificationService.showToast('Texte du lecteur chargé !', 'info');
      
      // Déclencher automatiquement la requête si demandé
      if (autoSend) {
        setTimeout(() => {
          sendMessage();
        }, 500);
      }
    } else {
      // Vérifier le localStorage pour le texte sélectionné
      const textFromStorage = localStorage.getItem('selectedTextForAI');
      const autoSendStorage = localStorage.getItem('autoSendAI') === 'true';
      
      if (textFromStorage) {
        setNewMessage(textFromStorage);
        localStorage.removeItem('selectedTextForAI');
        localStorage.removeItem('autoSendAI');
        notificationService.showToast('Texte du lecteur chargé !', 'info');
        
        // Déclencher automatiquement la requête si demandé
        if (autoSendStorage) {
          setTimeout(() => {
            sendMessage();
          }, 500);
        }
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamDisplay, loading]);

  useEffect(() => {
    if (!loading || reasoningMeta) return undefined;
    const id = setInterval(() => {
      setPhaseStep((s) => (s + 1) % 4);
    }, 1600);
    return () => clearInterval(id);
  }, [loading, reasoningMeta]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // === GESTION PROVIDERS IA ===

  const loadAIProviders = async () => {
    try {
      const data = await apiJSON('conversations/ai-providers');
      let providers = data.providers || {};
      
      // AUD-008: charger les modèles Ollama à jour depuis l'API
      try {
        const ollamaRes = await apiJSON('ai/ollama/models');
        if (ollamaRes?.success && Array.isArray(ollamaRes.models) && ollamaRes.models.length > 0) {
          providers = {
            ...providers,
            ollama: {
              ...(providers.ollama || { name: 'Ollama (Local)', description: 'Modèles locaux via Ollama' }),
              models: ollamaRes.models
            }
          };
        }
      } catch (_) {}
      
      setAiProviders(providers);
      
      // Définir provider par défaut
      if (data.default_provider && providers[data.default_provider]) {
        setSelectedProvider(data.default_provider);
        setSelectedModel(data.default_model || (providers[data.default_provider].models && providers[data.default_provider].models[0]));
      } else if (providers.ollama) {
        setSelectedProvider('ollama');
        setSelectedModel(providers.ollama.models?.[0] || 'llama3.1:8b');
      }
    } catch (error) {
              handleApiError(error, 'Chargement providers IA');
      console.error('Erreur load AI providers:', error);
      
      // Fallback: utiliser des providers par défaut
      setAiProviders({
        ollama: {
          name: "Ollama (Local)",
          models: ["llama3.1:8b", "mistral:7b"],
          description: "Modèles locaux via Ollama"
        }
      });
      setSelectedProvider('ollama');
      setSelectedModel('llama3.1:8b');
    }
  };

  // === GESTION CONVERSATIONS ===

  const loadConversations = async () => {
    try {
      const data = await apiJSON('conversations/list');
      setConversations(data.conversations || []);
      
      // Charger première conversation ou créer nouvelle
      if (data.conversations.length > 0 && !currentConversationId) {
        setCurrentConversationId(data.conversations[0].conversation_id);
      } else if (data.conversations.length === 0) {
        await createNewConversation();
      }
    } catch (error) {
      handleApiError(error, 'Chargement conversations');
      console.error('Erreur load conversations:', error);
      
      // Fallback: créer une conversation locale
      const fallbackConversation = {
        conversation_id: 'local-fallback',
        title: 'Conversation locale',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        total_messages: 0,
        context_books: []
      };
      setConversations([fallbackConversation]);
      setCurrentConversationId('local-fallback');
    }
  };

  const createNewConversation = async () => {
    try {
      const formData = new FormData();
      formData.append('title', 'Nouvelle conversation');

      const response = await apiFetch('conversations/create', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentConversationId(data.conversation_id);
        notificationService.showToast('✅ Nouvelle conversation créée', 'success');
        await loadConversations();
      }
    } catch (error) {
              handleApiError(error, 'Création conversation');
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const data = await apiJSON(`conversations/${conversationId}`);
      setMessages(data.messages || []);
      setCurrentConversationId(conversationId);
    } catch (error) {
              handleApiError(error, 'Chargement conversation');
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation ?')) {
      return;
    }

    try {
      const response = await apiFetch(`conversations/${conversationId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        notificationService.showToast('✅ Conversation supprimée', 'success');
        await loadConversations();
        
        // Si c'était la conversation courante, charger la première disponible
        if (conversationId === currentConversationId) {
          const data = await apiJSON('conversations/list');
          if (data.conversations.length > 0) {
            setCurrentConversationId(data.conversations[0].conversation_id);
          } else {
            await createNewConversation();
          }
        }
      }
    } catch (error) {
              handleApiError(error, 'Suppression conversation');
    }
  };

  const exportConversation = async (conversationId) => {
    try {
      const response = await apiFetch(`conversations/${conversationId}/export`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `conversation_${conversationId.slice(0, 8)}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        notificationService.showToast('✅ Conversation exportée', 'success');
      }
    } catch (error) {
              handleApiError(error, 'Export conversation');
    }
  };

  const updateConversationTitle = async (conversationId, newTitle) => {
    try {
      const formData = new FormData();
      formData.append('title', newTitle);

      const response = await apiFetch(`conversations/${conversationId}/title`, {
        method: 'PUT',
        body: formData
      });

      if (response.ok) {
        notificationService.showToast('✅ Titre mis à jour', 'success');
        await loadConversations();
        setEditingTitle(null);
      }
    } catch (error) {
              handleApiError(error, 'Mise à jour titre');
    }
  };

  // === GESTION MESSAGES ===

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentConversationId) return;

    const question = newMessage.trim();
    setNewMessage('');
    setLoading(true);
    setReasoningMeta(null);
    setReasoningCollapsed(false);
    setPhaseStep(0);
    setStreamingStarted(false);
    streamTextRef.current = '';
    firstStreamTokenRef.current = false;
    setStreamDisplay('');
    streamGenuiBlocksRef.current = [];
    genuiSigRef.current = new Set();
    setStreamGenuiBlocks([]);

    if (streamCancelRef.current) {
      streamCancelRef.current();
      streamCancelRef.current = null;
    }

    const tempQuestion = {
      message_id: 'temp_q_' + Date.now(),
      message_type: 'question',
      content: question,
      timestamp: Date.now() / 1000
    };
    setMessages((prev) => [...prev, tempQuestion]);

    const finishWithError = (err) => {
      handleApiError(err, 'Streaming RAG');
      setMessages((prev) => prev.filter((m) => m.message_id !== tempQuestion.message_id));
      setLoading(false);
      setStreamDisplay('');
    };

    try {
      streamCancelRef.current = api.streamAsk(
        {
          question,
          method: searchMethod,
          k: resultsCount,
          bookFilters: bookFilters.trim() || undefined,
          model: selectedModel || undefined,
        },
        {
          onChunk: (text) => {
            if (text && !firstStreamTokenRef.current) {
              firstStreamTokenRef.current = true;
              setReasoningCollapsed(true);
              setStreamingStarted(true);
            }
            streamTextRef.current += text;
            if (!streamRafRef.current) {
              streamRafRef.current = requestAnimationFrame(() => {
                streamRafRef.current = null;
                const { cleanedText, blocks } = tryExtractGenUIFromText(streamTextRef.current);
                blocks.forEach((b) => addGenUIBlock({ type: b.type, data: b.data }));
                setStreamDisplay(hideIncompleteGenui(cleanedText));
              });
            }
          },
          onGenUI: (payload) => {
            addGenUIBlock(payload);
          },
          onHighlight: (ev) => {
            if (
              ev.book_id != null &&
              ev.char_start != null &&
              ev.char_end != null
            ) {
              setStreamHighlight({
                bookId: String(ev.book_id),
                chunkIdx: Number(ev.chunk_idx),
                charStart: ev.char_start,
                charEnd: ev.char_end,
                requestId: Date.now(),
              });
            }
          },
          onMeta: (ev) => {
            setReasoningMeta(ev);
          },
          onDone: (ev) => {
            const rawAnswer = ev.answer || streamTextRef.current || '';
            const { cleanedText, blocks: fallbackBlocks } = tryExtractGenUIFromText(rawAnswer);
            fallbackBlocks.forEach((b) => addGenUIBlock({ type: b.type, data: b.data }));
            const text = cleanedText || rawAnswer;
            const ts = Date.now();
            const qMsgId = `q_${ts}`;
            const aMsgId = `ans_${ts}`;
            const answerMessage = {
              message_id: aMsgId,
              message_type: 'answer',
              content: text,
              timestamp: Date.now() / 1000,
              model_used: ev.model_used,
              processing_time_ms: ev.processing_time_ms,
              confidence_score: ev.confidence_score,
              citations: ev.citations || [],
              genuiBlocks: [...streamGenuiBlocksRef.current],
            };
            const persistentQuestion = {
              ...tempQuestion,
              message_id: qMsgId,
            };
            setMessages((prev) => {
              const withoutTemp = prev.filter(
                (m) => m.message_id !== tempQuestion.message_id
              );
              return [...withoutTemp, persistentQuestion, answerMessage];
            });

            if (
              currentConversationId &&
              !String(currentConversationId).startsWith('local-fallback')
            ) {
              api
                .commitStreamExchange(currentConversationId, {
                  question,
                  answer: text,
                  citations: ev.citations || [],
                  model_used: ev.model_used ?? null,
                  processing_time_ms: ev.processing_time_ms ?? null,
                  confidence_score: ev.confidence_score ?? null,
                  search_method: searchMethod,
                  book_filters: bookFilters.trim() || null,
                })
                .then((data) => {
                  if (data?.question_id && data?.answer_id) {
                    setMessages((prev) =>
                      prev.map((m) => {
                        if (m.message_id === qMsgId) {
                          return { ...m, message_id: data.question_id };
                        }
                        if (m.message_id === aMsgId) {
                          return { ...m, message_id: data.answer_id };
                        }
                        return m;
                      })
                    );
                  }
                  loadConversations();
                })
                .catch((e) => {
                  // Un échec de persistance n'était que consigné en console :
                  // l'utilisateur voyait « Réponse générée » et repartait en
                  // croyant l'échange enregistré.
                  console.warn('Persistance conversation:', e);
                  notificationService.showToast(
                    '⚠️ Réponse affichée mais non enregistrée dans la conversation',
                    'warning'
                  );
                });
            }

            setStreamDisplay('');
            streamTextRef.current = '';
            streamGenuiBlocksRef.current = [];
            genuiSigRef.current = new Set();
            setStreamGenuiBlocks([]);
            streamCancelRef.current = null;
            setLoading(false);
            setStreamingStarted(false);
            notificationService.showToast(
              `✅ Réponse générée (${ev.processing_time_ms || 0}ms)`,
              'success'
            );
          },
          onError: (err) => {
            const msg =
              err && err.message ? err.message : String(err);
            finishWithError(new Error(msg));
            streamCancelRef.current = null;
          },
        }
      );
    } catch (error) {
      finishWithError(error);
    }
  };

  const openCitationInReader = (citation) => {
    setStreamHighlight({
      bookId: String(citation.book_id),
      chunkIdx: Number(citation.chunk_idx),
      charStart: citation.char_start ?? 0,
      charEnd: citation.char_end ?? 0,
      requestId: Date.now(),
    });
  };

  // === RENDU UI ===

  const getMessageIcon = (type) => {
    switch (type) {
      case 'question': return '👤';
      case 'answer': return '🤖';
      case 'error': return '❌';
      default: return '💬';
    }
  };

  const getMessageColor = (type) => {
    switch (type) {
      case 'question': return '#8B5CF6';
      case 'answer': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="glass-panel" style={{ 
      display: 'flex', 
      height: '100vh', 
      fontFamily: 'system-ui, sans-serif',
      background: 'transparent' 
    }}>
      
      {/* Sidebar conversations */}
      {showSidebar && (
        <div className="glass-panel" style={{
          width: '300px',
          background: 'transparent',
          borderRight: '2px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
        }}>
          
          {/* Header sidebar */}
          <div className="glass-panel" style={{ 
            padding: '20px', 
            borderBottom: '2px solid #e5e7eb',
            background: 'transparent'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: '700',
                color: '#111827'
              }}>
                💬 Conversations
              </h2>
              <button
                onClick={createNewConversation}
                style={{
                  background: '#7C3AED',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#6D28D9'}
                onMouseOut={(e) => e.target.style.background = '#7C3AED'}
              >
                + Nouveau
              </button>
            </div>
          </div>
          
          {/* Liste conversations */}
          <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
            {conversations.map(conv => (
              <div
                key={conv.conversation_id}
                style={{
                  padding: '16px',
                  margin: '6px 0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  background: currentConversationId === conv.conversation_id ? '#eff6ff' : 'transparent',
                  border: currentConversationId === conv.conversation_id ? '2px solid #7C3AED' : '2px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                  boxShadow: currentConversationId === conv.conversation_id ? '0 4px 12px rgba(124, 58, 237, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
                onClick={() => loadConversation(conv.conversation_id)}
                onMouseOver={(e) => {
                  if (currentConversationId !== conv.conversation_id) {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentConversationId !== conv.conversation_id) {
                    e.target.style.background = 'transparent';
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    {editingTitle === conv.conversation_id ? (
                      <input
                        type="text"
                        defaultValue={conv.title}
                        onBlur={(e) => updateConversationTitle(conv.conversation_id, e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && updateConversationTitle(conv.conversation_id, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '2px solid #7C3AED',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          outline: 'none'
                        }}
                        autoFocus
                      />
                    ) : (
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '15px',
                        color: '#111827',
                        lineHeight: '1.4'
                      }}>
                        {conv.title}
                      </div>
                    )}
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#4b5563',
                      marginTop: '6px',
                      fontWeight: '500'
                    }}>
                      {new Date(conv.updated_at * 1000).toLocaleDateString('fr-FR')} • {conv.total_messages} messages
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTitle(conv.conversation_id);
                      }}
                      style={{
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        cursor: 'pointer',
                        padding: '6px',
                        color: '#374151',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#e5e7eb';
                        e.target.style.color = '#111827';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.color = '#374151';
                      }}
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        exportConversation(conv.conversation_id);
                      }}
                      style={{
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        cursor: 'pointer',
                        padding: '6px',
                        color: '#374151',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#e5e7eb';
                        e.target.style.color = '#111827';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.color = '#374151';
                      }}
                    >
                      <Download size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conv.conversation_id);
                      }}
                      style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        cursor: 'pointer',
                        padding: '6px',
                        color: '#dc2626',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#fee2e2';
                        e.target.style.color = '#b91c1c';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = '#fef2f2';
                        e.target.style.color = '#dc2626';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
              {/* Zone principale */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          
          {/* Header principal */}
          <div style={{
            padding: '20px',
            background: 'transparent',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  padding: '10px',
                  borderRadius: '8px',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#e5e7eb';
                  e.target.style.color = '#111827';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
              >
                <MessageCircle size={20} />
              </button>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: '700',
                color: '#111827'
              }}>
                🤖 IA Conversation
              </h1>
            </div>
            
            {/* Paramètres IA */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => setShowAISettings(!showAISettings)}
                style={{
                  background: 'transparent',
                  border: '2px solid #e5e7eb',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#f1f5f9';
                  e.target.style.borderColor = '#cbd5e1';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                <SettingsIcon size={16} />
                IA
              </button>
              
              <button
                onClick={() => setShowAISelector(true)}
                style={{
                  background: '#7C3AED',
                  color: 'white',
                  border: '2px solid #6D28D9',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#6D28D9'}
                onMouseOut={(e) => e.target.style.background = '#7C3AED'}
              >
                <Globe size={16} />
                Choisir IA
              </button>
              
              {/* La case « Web » a été retirée : son état ne servait qu'à colorer
                  une icône. Ni /api/rag/ask ni /api/rag/ask/stream n'acceptent de
                  paramètre de recherche web — la case ne pouvait rien changer à
                  la réponse. Les outils web vivent dans l'écran Perplexity. */}

              {/* Portée de la question : livre unique le cas échéant */}
              {bookFilters && (
                <div
                  data-testid="portee-livre"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '2px solid #bfdbfe',
                    background: '#eff6ff',
                    fontSize: '14px',
                    color: '#1e40af',
                  }}
                >
                  <span>Question limitée à un livre</span>
                  <button
                    type="button"
                    onClick={() => setBookFilters('')}
                    style={{ fontSize: '12px', cursor: 'pointer' }}
                  >
                    Toute la bibliothèque
                  </button>
                </div>
              )}
            </div>
          </div>
        
        {/* Paramètres IA (collapsible) */}
        {showAISettings && (
          <div style={{
            padding: '24px',
            background: 'transparent',
            borderBottom: '2px solid #e5e7eb',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: '#111827'
            }}>
              ⚙️ Configuration IA
            </h3>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  display: 'block', 
                  marginBottom: '8px' 
                }}>
                  Provider IA
                </label>
                <select
                  value={selectedProvider}
                  onChange={(e) => {
                    setSelectedProvider(e.target.value);
                    if (e.target.value && aiProviders[e.target.value]) {
                      setSelectedModel(aiProviders[e.target.value].models[0]);
                    }
                  }}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minWidth: '180px',
                    background: 'transparent',
                    color: '#111827',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                >
                  <option value="">Provider par défaut</option>
                  {Object.entries(aiProviders).map(([id, provider]) => (
                    <option key={id} value={id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  display: 'block', 
                  marginBottom: '8px' 
                }}>
                  Modèle
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minWidth: '180px',
                    background: 'transparent',
                    color: '#111827',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                >
                  {selectedProvider && aiProviders[selectedProvider]?.models?.length ? (
                    aiProviders[selectedProvider].models.map(model => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))
                  ) : (
                    <option value="">Sélectionner un provider</option>
                  )}
                </select>
              </div>
              
              {/* Lien « Synthèse externe » retiré avec la route /perplexity :
                  ces outils dépendent d'une clé Perplexity absente, le lien
                  n'aurait mené qu'à la page « introuvable ». */}
              
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  display: 'block', 
                  marginBottom: '8px' 
                }}>
                  Méthode de recherche
                </label>
                <select
                  value={searchMethod}
                  onChange={(e) => setSearchMethod(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minWidth: '140px',
                    background: 'transparent',
                    color: '#111827',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                >
                  <option value="hybrid">Hybride</option>
                  <option value="vector">Vectorielle</option>
                  <option value="keyword">Mots-clés</option>
                </select>
              </div>
              
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  display: 'block', 
                  marginBottom: '8px' 
                }}>
                  Nombre de résultats
                </label>
                <input
                  type="number"
                  value={resultsCount}
                  onChange={(e) => setResultsCount(parseInt(e.target.value) || 5)}
                  min="1"
                  max="20"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    width: '100px',
                    background: 'transparent',
                    color: '#111827',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Messages — zone ciblée par le tutoriel (citations / réponses) */}
        <div
          className="tour-citation-zone"
          style={{
          flex: 1, 
          overflow: 'auto', 
          padding: '24px',
          background: '#fafafa'
        }}
        >
          {messages.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
              color: '#6b7280',
              background: 'transparent',
              borderRadius: '16px',
              border: '2px solid #e5e7eb',
              margin: '20px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <MessageCircle size={64} style={{ 
                marginBottom: '20px',
                color: '#9ca3af'
              }} />
              <h3 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '22px',
                fontWeight: '700',
                color: '#374151'
              }}>
                Commencez une conversation
              </h3>
              <p style={{ 
                margin: 0, 
                fontSize: '16px',
                color: '#6b7280',
                lineHeight: '1.6',
                maxWidth: '400px',
                margin: '0 auto'
              }}>
                Posez une question à votre bibliothèque et l'IA vous répondra en se basant sur vos documents.
              </p>
            </div>
          ) : (
            messages.map(message => (
              <div
                key={message.message_id}
                style={{
                  marginBottom: '20px',
                  padding: '20px',
                  borderRadius: '16px',
                  background: message.message_type === 'question' ? 'transparent' : 'transparent',
                  border: message.message_type === 'question' ? '2px solid #dbeafe' : '2px solid #e5e7eb',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: getMessageColor(message.message_type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '18px',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    {getMessageIcon(message.message_type)}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '12px',
                      fontWeight: '500'
                    }}>
                      {new Date(message.timestamp * 1000).toLocaleString('fr-FR')}
                      {message.model_used && (
                        <span style={{ 
                          marginLeft: '12px', 
                          color: '#059669',
                          fontWeight: '600'
                        }}>
                          • {message.model_used}
                        </span>
                      )}
                    </div>
                    
                    <div style={{
                      fontSize: '16px',
                      lineHeight: '1.7',
                      color: '#111827',
                      whiteSpace: 'pre-wrap',
                      fontWeight: '500'
                    }}>
                      {message.message_type === 'answer' && message.genuiBlocks && message.genuiBlocks.length > 0 && (
                        <Suspense fallback={null}>
                          <GenUIMessagePart blocks={message.genuiBlocks} />
                        </Suspense>
                      )}
                      {message.content}
                    </div>
                    
                    {/* Citations */}
                    {message.citations && message.citations.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '700',
                          color: '#111827',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          📚 Sources ({message.citations.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {message.citations.map((citation, index) => (
                            <div
                              key={index}
                              className={index === 0 ? 'tour-citation-link' : undefined}
                              role="button"
                              tabIndex={0}
                              onClick={() => openCitationInReader(citation)}
                              onKeyDown={(e) => e.key === 'Enter' && openCitationInReader(citation)}
                              style={{
                                padding: '16px',
                                background: 'transparent',
                                borderRadius: '12px',
                                border: '2px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.borderColor = '#d1d5db';
                                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                              }}
                            >
                              <div style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: '#111827',
                                marginBottom: '6px'
                              }}>
                                {citation.book_title}
                                {citation.book_author && ` par ${citation.book_author}`}
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: '#6b7280',
                                marginBottom: '10px',
                                fontWeight: '600'
                              }}>
                                {citation.page_start && citation.page_end ? 
                                  `📄 Pages ${citation.page_start}-${citation.page_end}` : 
                                  '📄 Extrait'
                                }
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#374151',
                                fontStyle: 'italic',
                                lineHeight: '1.6',
                                background: 'transparent',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid #f3f4f6'
                              }}>
                                "{citation.text}"
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Métadonnées */}
                    {message.processing_time_ms && (
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: '8px',
                        display: 'flex',
                        gap: '12px'
                      }}>
                        <span>⏱️ {message.processing_time_ms}ms</span>
                        {message.confidence_score && (
                          <span>🎯 {Math.round(message.confidence_score * 100)}% confiance</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <>
              <ReasoningTrace
                meta={reasoningMeta}
                phaseStep={phaseStep}
                collapsed={reasoningCollapsed}
                streamingStarted={streamingStarted}
                onToggle={() => setReasoningCollapsed((c) => !c)}
              />
              {(streamDisplay || streamingStarted || streamGenuiBlocks.length > 0) && (
                <div className="streaming-answer-preview" aria-live="polite">
                  {streamGenuiBlocks.length > 0 && (
                    <Suspense fallback={null}>
                      <GenUIMessagePart blocks={streamGenuiBlocks} />
                    </Suspense>
                  )}
                  <span className="stream-text-chunk">{streamDisplay}</span>
                  {loading && (streamingStarted || streamDisplay) && (
                    <span className="stream-cursor" aria-hidden="true" />
                  )}
                </div>
              )}
              {!streamDisplay && !streamingStarted && (
                <div
                  className="genui-thinking-pulse"
                  style={{
                  padding: '20px',
                  borderRadius: '16px',
                  background: 'transparent',
                  border: '2px solid #dbeafe',
                  textAlign: 'center',
                  color: '#374151',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                  margin: '20px 24px'
                }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid #dbeafe',
                      borderTop: '3px solid #7C3AED',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827'
                    }}>
                      Connexion au flux RAG…
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div style={{
          padding: '24px',
          background: 'transparent',
          borderTop: '2px solid #e5e7eb',
          boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <input
              type="text"
              className="tour-chat-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Posez votre question à l'IA..."
              disabled={loading}
              style={{
                flex: 1,
                padding: '16px 20px',
                border: '2px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '16px',
                outline: 'none',
                background: 'transparent',
                color: '#111827',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#7C3AED';
                e.target.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || loading}
              style={{
                padding: '16px 24px',
                background: newMessage.trim() && !loading ? '#7C3AED' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: newMessage.trim() && !loading ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: newMessage.trim() && !loading ? '0 4px 12px rgba(124, 58, 237, 0.2)' : 'none',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                if (newMessage.trim() && !loading) {
                  e.target.style.background = '#6D28D9';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseOut={(e) => {
                if (newMessage.trim() && !loading) {
                  e.target.style.background = '#7C3AED';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              <Send size={20} />
              Envoyer
            </button>
          </div>
        </div>
      </div>
      
      {/* Sélecteur IA Modal */}
      {showAISelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'transparent',
            borderRadius: '20px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '2px solid #e5e7eb'
          }}>
            <div style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              zIndex: 1
            }}>
              <button
                onClick={() => setShowAISelector(false)}
                style={{
                  background: '#f3f4f6',
                  border: '2px solid #e5e7eb',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '8px',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#e5e7eb';
                  e.target.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>
            
            <AISelector
              providers={aiProviders}
              selectedProvider={selectedProvider}
              selectedModel={selectedModel}
              onProviderChange={(provider) => {
                setSelectedProvider(provider);
                setShowAISelector(false);
                notificationService.showToast(`✅ IA sélectionnée: ${provider}`, 'success');
              }}
              onModelChange={(model) => {
                setSelectedModel(model);
                notificationService.showToast(`✅ Modèle sélectionné: ${model}`, 'success');
              }}
            />
          </div>
        </div>
      )}
      
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 16px',
          borderRadius: '8px',
          background: toast.type === 'error' ? '#ef4444' : toast.type === 'success' ? '#10b981' : '#8B5CF6',
          color: 'white',
          fontSize: '14px',
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          {toast.message}
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IA;
